import classNames from 'classnames';
import React, { Component } from 'react';
import { debounce } from 'lodash/fp';
import QuestionElementPrompt from 'components/Questions/shared/QuestionElementPrompt';
import { answerUploadToFilestack } from 'misc/answerUpload';
import { Button, Icon, Modal, ButtonGroup } from 'ui';
import { Text, TEXT_VARIANTS } from 'ui/Typography';
import { routes } from 'constants/config';
import request from 'utils/request_ajax';
import { hasFeatureFlags } from 'utils/features';

import getExtensionFromFilename from 'utils/getExtensionFromFilename';
import { FileExtensionsToMimeType } from 'constants/fileExtensionsToMimeType';
import { UniversalUploader } from 'components/shared/UniversalUploader';
import OpenDownload from './OpenDownload';

const getMimeTypeFromFileName = filename => {
  const extension = getExtensionFromFilename(filename);

  return FileExtensionsToMimeType[extension];
};

class UploadResponseQuestion extends Component {
  state = {
    uploadedFileName: null,
    showDiscardModule: false,
    hasFileUploadFF: hasFeatureFlags([
      'learning-file-stack-upload-replacement',
    ]),
  };

  componentDidMount() {
    this.setState({
      uploadedFileName: this.props.data.givenAnswer,
    });
  }

  constructor(props) {
    super(props);
    this.handleAnswerUpload = this.handleAnswerUpload.bind(this);
    this.saveAnswer = debounce(300, this.saveAnswer.bind(this));
    this.uploadAsJson = this.uploadAsJson.bind(this);
    this.discardUpload = this.discardUpload.bind(this);
    this.toggleModal = this.toggleModal.bind(this);
    this.saveBssAnswer = this.saveBssAnswer.bind(this);
    this.deleteBssAnswer = this.deleteBssAnswer.bind(this);
  }

  saveAnswer(metadata) {
    const answer = metadata.upload_file_name;
    const answerData = {
      answer,
      question_id: this.props.data.questionId,
    };

    request
      .postJSON(this.props.meta.saveUrl, answerData)
      .then(() => {
        request
          .postJSON(routes.ANSWER_UPLOAD(this.props.data.questionId), metadata)
          .then(() => {
            this.setState({
              uploadedFileName: metadata.upload_file_name,
            });
          })
          .catch(e => {
            alert(t('questions.question.upload_response.upload_file_error')); // eslint-disable-line no-alert
          });
      })
      .catch(() => {
        alert(t('questions.question.upload_response.upload_file_error')); // eslint-disable-line no-alert
      });
  }

  saveBssAnswer(newUpload) {
    return new Promise(async (resolve, reject) => {
      const { bssUploadId } = newUpload;
      const { name } = newUpload.file;
      const ERROR_MESSAGE = 'Error Uploading File';
      try {
        await request.postJSON(this.props.meta.saveUrl, {
          answer: name,
          question_id: this.props.data.questionId,
        });
        const { success } = await request.postJSON(
          routes.ANSWER_UPLOAD(this.props.data.questionId),
          {
            bss_upload_id: bssUploadId,
          },
        );
        if (!success) reject(new Error(ERROR_MESSAGE));
        resolve();
      } catch (e) {
        reject(new Error(e || ERROR_MESSAGE));
      }
    });
  }

  deleteBssAnswer() {
    return new Promise(async (resolve, reject) => {
      const ERROR_MESSAGE = 'Error Deleting File';
      try {
        const { success } = await request.delete(
          routes.ANSWER_UPLOAD(this.props.data.questionId),
        );
        if (!success) reject(new Error(ERROR_MESSAGE));
        resolve();
      } catch (e) {
        reject(new Error(e.message || ERROR_MESSAGE));
      }
    });
  }

  uploadAsJson(upload) {
    const { filename } = upload;

    return {
      upload_file_name: filename,
      // For some reason mimetype is undefined from the upload object when we upload office files e.g .ppt, .xls, .rtf, etc
      // from a Windows PC so we added getMimeTypeFromFileName as a fallback.
      upload_file_type: upload.mimetype || getMimeTypeFromFileName(filename),
      upload_file_size: upload.size,
      upload_key: upload.key || '',
      upload_source: upload.source,
      upload_container: upload.container || '',
      filestack_handle: upload.handle,
    };
  }

  handleAnswerUpload() {
    answerUploadToFilestack().then(upload => {
      const metadata = this.uploadAsJson(upload);
      this.saveAnswer(metadata);
    });
  }

  discardUpload() {
    if (this.props.meta.finished) return;
    request
      .delete(routes.ANSWER_UPLOAD(this.props.data.questionId))
      .then(data => {
        this.setState({
          uploadedFileName: null,
          showDiscardModule: false,
        });
      })
      .catch(() => {
        this.setState({
          showDiscardModule: false,
        });
        // eslint-disable-next-line no-alert
        alert(t('questions.question.upload_response.discard_file_error'));
      });
  }

  toggleModal() {
    if (this.props.meta.finished || this.props.meta.previewing) return;
    this.setState(state => ({
      showDiscardModule: !state.showDiscardModule,
    }));
  }

  render() {
    const { meta } = this.props;
    const { uploadedFileName, showDiscardModule, hasFileUploadFF } = this.state;
    const disabledLinkClass = classNames({
      'QuestionElement-icon-disabled': meta.finished,
    });
    const { bssUploadId } = this.props?.data;
    const displayFileStackUploader =
      !hasFileUploadFF || (uploadedFileName && !bssUploadId);

    return (
      <>
        <QuestionElementPrompt
          elementHeaderClass={this.props.meta.elementHeaderClass}
          isGradable={this.props.data.isGradable}
          isPractice={this.props.data.isPractice}
          labelErrorClass={this.props.meta.labelErrorClass}
          questionSubjectClass={this.props.meta.questionSubjectClass}
          required={this.props.data.required}
          subject={this.props.data.subject}
          questionType={this.props.data.questionType}
        />

        <div className="UploadResponseQuestion">
          <div className="Response">
            <Text
              className="Response-instructions"
              variant={TEXT_VARIANTS.BODY_LIGHT}
            >
              {t('questions.question.upload_response.upload_file_subtitle')}
            </Text>
          </div>
          <div className="pl-32 pr-32 pb-40 fd-r">
            {displayFileStackUploader && uploadedFileName && (
              <div className="QuestionElement-uploadActions border-1 border-gray-light border-solid bg-gray-lightest p-10 fd-r jc-sb wb-bw w-75p">
                <div className="fz-18">
                  <OpenDownload
                    questionId={this.props.data.questionId}
                    fileName={this.state.uploadedFileName}
                    finished={meta.finished}
                  />
                </div>
                <span className="QuestionElement-icon">
                  <Icon
                    name="delete"
                    onClick={this.toggleModal}
                    onKeyDown={e => {
                      setTimeout(() => {
                        if (e.key === 'Enter') {
                          this.toggleModal();
                        }
                      }, 0);
                    }}
                    className={disabledLinkClass}
                    tabIndex="0"
                  />
                </span>
              </div>
            )}
            {displayFileStackUploader && !uploadedFileName && (
              <div className="QuestionElement-uploadActions">
                {!hasFileUploadFF && (
                  <button
                    type="button"
                    children={t(
                      'questions.question.upload_response.upload_file',
                    )}
                    className="AssignModal-button NewButton NewButton--lessonlyBrandColor NewButton--wide NewButton--bold CustomColor--backgroundColor CustomColor--buttonBoxShadow"
                    onClick={this.handleAnswerUpload}
                    disabled={meta.previewing || meta.finished}
                  />
                )}
              </div>
            )}
            {!displayFileStackUploader && (
              <div className="QuestionElement-uploadActions">
                <UniversalUploader
                  accept={[
                    '.pdf',
                    '.ppt',
                    '.pptx',
                    '.xls',
                    '.xlsx',
                    '.doc',
                    '.docx',
                    '.odt',
                    '.odp',
                    '.ods',
                    '.txt',
                    '.rtf',
                    '.jpg',
                    '.jpeg',
                    '.png',
                  ]}
                  onComplete={this.saveBssAnswer}
                  onDelete={this.deleteBssAnswer}
                  uploaderId={bssUploadId}
                  readOnly={meta.previewing || meta.finished}
                />
              </div>
            )}
          </div>
          <Modal
            className="w-488"
            shown={showDiscardModule}
            onDismiss={this.toggleModal}
            manualAutoFocus
          >
            <Modal.Header>
              {t('questions.question.upload_response.discard_file_title')}
            </Modal.Header>
            <Modal.Content className="d-f fd-c">
              <span className="d-f fd-c fz-14 ff-ss">
                {t(
                  'questions.question.upload_response.discard_file_confirmation',
                )}
              </span>
            </Modal.Content>
            <Modal.Footer>
              <ButtonGroup alignEnd className="ai-c">
                <Button
                  tall
                  onClick={this.toggleModal}
                  disabled={meta.previewing}
                  className="w-125"
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  tall
                  primary
                  onClick={this.discardUpload}
                  disabled={meta.previewing}
                  className="w-125"
                  autoFocus
                >
                  {t('common.delete')}
                </Button>
              </ButtonGroup>
            </Modal.Footer>
          </Modal>
        </div>
      </>
    );
  }
}

export default UploadResponseQuestion;
